/* eslint-disable react/no-danger */
import React from 'lib/react';
import {
  PRODUCT_IDS, IMAGE_FILES_PATH, COMMERCIAL, RESIDENTIAL,
} from 'constants/AppConstants';
import { EVAlert } from 'lib/components';
import {
  WALLS_PRODUCT_IDS, SOLAR_PRODUCT_TYPE, WHOLE_HOME_PRODUCT_IDS,
  REGULAR, EXPRESS, QUICK, THREE_HOUR,
} from './ProductList.constants';
import productTooltips from './productTooltips.json';
import { getDefaultMeasurementRequestTypes } from '../../Order.utils';

function getAlertMessage(toolTipInfo) {
  return (
    <div id="alertContainer">
      <EVAlert severity="info">
        {toolTipInfo}
      </EVAlert>
    </div>
  );
}

export const getAlertMessageForQSMF = () => getAlertMessage(productTooltips[PRODUCT_IDS.QuickSquaresMultiFamily].info);

export const filterRoofProducts = (isBlueprintUpload) => (product) => (
  product.IsRoofProduct
  && product.ProductType !== SOLAR_PRODUCT_TYPE
  && (isBlueprintUpload || product.ProductID !== PRODUCT_IDS.BlueprintResidential)
);

export function filterSolarProducts(product) {
  return product.IsRoofProduct && product.ProductType === SOLAR_PRODUCT_TYPE;
}

export function filterWallsProducts(product) {
  return (!product.IsRoofProduct) && WALLS_PRODUCT_IDS.indexOf(product.ProductID) !== -1;
}

export function filterOtherProducts(product) {
  return (!product.IsRoofProduct)
    && WALLS_PRODUCT_IDS.indexOf(product.ProductID) === -1
    && WHOLE_HOME_PRODUCT_IDS.indexOf(product.ProductID) === -1
    && product.ProductID !== PRODUCT_IDS.PropertyData;
}

export function filterDataProducts(product, propertyType) {
  // Hide Property Data for Commercial property type
  if (product.ProductID === PRODUCT_IDS.PropertyData && propertyType === COMMERCIAL) {
    return false;
  }
  return product.ProductID === PRODUCT_IDS.PropertyData;
}

// #genai - Filter NotSet products based on property type restrictions
export function filterNotSetProducts(product, propertyType, isESXEnabled = false) {
  // ESX (114) should only be available for Residential property type
  if (product.ProductID === PRODUCT_IDS.ESX) {
    return propertyType === RESIDENTIAL && isESXEnabled;
  }
  return true;
}

export function filterWholeHomeProducts(product) {
  return WHOLE_HOME_PRODUCT_IDS.indexOf(product.ProductID) !== -1;
}

/**
 * Groups products into categories based on certain filters.
 * @param {boolean} isBlueprintUpload - Indicates if the product is a blueprint upload.
 * @param {string} propertyType - The property type (Residential/Commercial).
 * @returns {Function} - A function that takes a product and returns its category.
 */
// genAI
export const groupProductsIntoCategories = (products) => (isBlueprintUpload, propertyType) => {
  // Initialize an empty object to hold grouped products
  const groupedProducts = {};

  // Iterate over each product
  products.forEach((product) => {
    // Determine the category for the product
    let category;
    if (filterRoofProducts(isBlueprintUpload)(product)) {
      category = 'roof';
    } else if (filterSolarProducts(product)) {
      category = 'solar';
    } else if (filterWallsProducts(product)) {
      category = 'walls';
    } else if (filterDataProducts(product, propertyType)) {
      category = 'data';
    } else if (filterOtherProducts(product)) {
      category = 'others';
    } else if (filterWholeHomeProducts(product)) {
      category = 'wholeHome';
    }

    // Add the product to the corresponding category
    if (category) {
      // Create the category array if it doesn't exist
      if (!groupedProducts[category]) {
        groupedProducts[category] = [];
      }
      // Push the product to the category array
      groupedProducts[category].push(product);
    }
  });

  return groupedProducts;
};

export function getFormatedDeliveryType(deliveryType) {
  if (deliveryType.includes(REGULAR)) {
    return REGULAR;
  }
  if (deliveryType.includes(EXPRESS)) {
    return EXPRESS;
  }
  if (deliveryType.includes(QUICK)) {
    return QUICK;
  }
  return THREE_HOUR;
}

export function getTooltipForProduct(productId) {
  const productTooltip = productTooltips[productId];
  // #genai: return null so every path yields a React node (Sonar S3800); callers coerce to '' for MUI's empty-title check
  if (!productTooltip?.content) return null;
  // Special handling for Property Data - bullet points without image/sample report
  if (parseInt(productId, 10) === PRODUCT_IDS.PropertyData) {
    return (
      <div>
        <div id="tooltipContent">
          <div id="textContainer" style={{ display: 'flex', gap: '20px', flexDirection: 'column' }}>
            <div style={{ color: 'black', fontSize: '14px' }}>{productTooltip.name}</div>
            {productTooltip.info && getAlertMessage(productTooltip.info)}
            <div id="productDetails">
              {productTooltip.content.map((line) => (
                <div key={line}>
                  {/* #genai Render exactly one explicit space between the bullet and text. */}
                  <span aria-hidden="true">
                    &#8226;
                  </span>
                  {' '}
                  <span dangerouslySetInnerHTML={{ __html: line }} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div id="tooltipFooter">{productTooltip.footer}</div>
      </div>
    );
  }
  if (productTooltip.type.includes('styled')) {
    return (
      <div>
        <div id="tooltipContent">
          <div id="productImageContainer">
            <img
              style={{ height: '100%', width: '100%' }}
              alt="Premium"
              src={IMAGE_FILES_PATH + productTooltip.imageUrl}
            />
          </div>

          <div id="textContainer" style={{ display: 'flex', gap: '20px', flexDirection: 'column' }}>
            <div style={{ color: 'black', fontSize: '14px' }}>{productTooltip.name}</div>
            {productTooltip.info && getAlertMessage(productTooltip.info)}
            <div id="productDetails">
              {productTooltip.content.map((line) => (
                <div key={line}>
                  &#8226;
                  <span dangerouslySetInnerHTML={{ __html: line }} />
                </div>
              ))}
            </div>
            {productTooltip.sampleReport && (
              <a className="sample-report" lang="en" href={productTooltip.sampleReport} target="_blank" rel="noreferrer">
                {productTooltip.name.includes('TrueDesign') ? 'Schedule Demo' : 'Download Sample Report'}
                <img style={{ padding: '0 4px' }} alt="Pdf Icon" src={IMAGE_FILES_PATH + productTooltip.sampleImageUrl} />
              </a>
            )}
          </div>
        </div>
        <div id="tooltipFooter">{productTooltip.footer}</div>
      </div>
    );
  }
  return (
    <div id="tooltipContent">
      {productTooltip.imageUrl && (
        <div id="productImageContainer">
          <img
            style={{ height: '100%', width: '100%' }}
            alt="Premium"
            src={IMAGE_FILES_PATH + productTooltip.imageUrl}
          />

        </div>
      )}
      <div id="textContainer">
        <strong style={{ color: 'black' }}>{productTooltip.name}</strong>
        <div id="productDetails">
          <div>{productTooltip.content}</div>
          {productTooltip.sampleReport && (
            <a className="sample-report" lang="en" href={productTooltip.sampleReport} target="_blank" rel="noreferrer">
              {productTooltip.name.includes('TrueDesign') ? 'Schedule Demo' : 'Download Sample Report'}
              <img style={{ padding: '0 4px' }} alt="Pdf Icon" src={IMAGE_FILES_PATH + productTooltip.sampleImageUrl} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Retrieves pre-selected products based from user products for selected property type.
 * @param {Object} userProducts - The user's products.
 * @param {string} propertyType - The property type.
 * @returns {Object} - The pre-selected products.
 */
export const getPreSelectedProducts = (userProducts, propertyType) => {
  // #genai - Safely read the optional NotSet product collection.
  const notSetProducts = userProducts.NotSet?.PrimaryProducts
    ? userProducts.NotSet.PrimaryProducts.filter((p) => filterNotSetProducts(p, propertyType))
    : [];
  const propertyTypeProducts = [
    ...(userProducts[propertyType]?.PrimaryProducts || []),
    ...notSetProducts,
  ];

  const selectedUserProducts = propertyTypeProducts.filter(({ IsDefault }) => IsDefault);

  const {
    roof = [], solar = [], wholeHome = [], walls = [], others = [],
  } = groupProductsIntoCategories(selectedUserProducts)(false, propertyType);

  const selectedPrimaryProduct = [...roof, ...solar, ...wholeHome][0];
  const selectedPrimaryDeliveryProduct = selectedPrimaryProduct?.WebDeliveryProducts.find(({ IsDefault }) => IsDefault)?.ProductID
      || selectedPrimaryProduct?.WebDeliveryProducts[0]?.ProductID;

  const selectedWallsProduct = walls[0];
  const selectedWallsDeliveryProduct = selectedWallsProduct?.WebDeliveryProducts.find(({ IsDefault }) => IsDefault)?.ProductID
  || selectedWallsProduct?.WebDeliveryProducts[0]?.ProductID;

  const selectedOtherProducts = others.reduce((selctedOthers, currentProduct) => (
    (currentProduct.IsDefault)
      ? ({
        ...selctedOthers,
        [currentProduct.ProductID]: currentProduct?.WebDeliveryProducts.find(({ IsDefault }) => IsDefault)?.ProductID
        || currentProduct?.WebDeliveryProducts[0]?.ProductID,
      })
      : selctedOthers
  ), {});

  return {
    ...(selectedPrimaryProduct ? { selectedPrimaryProduct, selectedPrimaryDeliveryProduct } : {}),
    ...(selectedWallsProduct ? { selectedWallsProduct, selectedWallsDeliveryProduct } : {}),
    ...(selectedOtherProducts ? { selectedOtherProducts } : {}),
    ...getDefaultMeasurementRequestTypes(selectedPrimaryProduct?.ProductID),
  };
};

export const getSolarProductIds = (userProducts) => {
  if (!userProducts) return null;
  const solarProductIds = [];

  if (userProducts.Residential) {
    userProducts.Residential.PrimaryProducts.forEach((product) => {
      if (filterSolarProducts(product)) {
        solarProductIds.push(product.ProductID);
      }
    });
  }
  return solarProductIds;
};
